import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Form, Button, Modal, Card, Spinner, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { IoIosAddCircleOutline } from "react-icons/io";
import IconCard from '../../images/icon-card.png';
import IconDelete from '../../images/icon-delete.png';
import { toast } from "react-toastify";
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements
} from "@stripe/react-stripe-js";
import axios from 'axios';
import apiConfig from '../credentials/apiConfig';


const PaymentMethodsTab = () => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [methodToDelete, setMethodToDelete] = useState(null);


    useEffect(() => {
        const fetchMethods = async () => {
            try {
                const authToken = localStorage.getItem("authToken");
                const userId = localStorage.getItem('userId');
                const response = await axios.get(`${apiConfig.apiBaseURL}/getAllPaymentMethods`, {
                    headers: {
                        "x-api-key": apiConfig["x-api-key"],
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`,
                        userId
                    }
                });
                if (response.data.success) {
                    setPaymentMethods(response.data.paymentMethods);
                }
            } catch (err) {
                console.error(err);
                toast.error("Failed to load payment methods.");
            } finally {
                setLoading(false);
            }
        };
        fetchMethods();
    }, []);

    const handleOpenDeleteModal = (id) => {
        setMethodToDelete(id);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            const userId = localStorage.getItem('userId');
            await axios.post(`${apiConfig.apiBaseURL}/softDeletePaymentMethod`, {
                paymentMethodId: methodToDelete
            }, {
                headers: {
                    "x-api-key": apiConfig["x-api-key"],
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${authToken}`,
                    userId
                }
            });
            setPaymentMethods(prev => prev.filter(m => m.methodId !== methodToDelete));
            toast.success('Payment method deleted successfully!');
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete payment method.");
        }
        setShowDeleteModal(false);
    };

    const handleSetDefault = async (id) => {
        try {
            const authToken = localStorage.getItem("authToken");
            const userId = localStorage.getItem('userId');
            await axios.put(`${apiConfig.apiBaseURL}/updatePrimaryPaymentMethod`, {
                paymentMethodId: id
            }, {
                headers: {
                    "x-api-key": apiConfig["x-api-key"],
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${authToken}`,
                    userId
                }
            });
            setPaymentMethods(prev => prev.map(m => ({ ...m, isPrimary: m.methodId === id })));
            toast.success('Default payment method updated!');
        } catch (err) {
            console.error(err);
            toast.error("Failed to update default method.");
        }
    };

    const addPaymentMethod = (method) => {
        setPaymentMethods(prev => method.isPrimary
            ? [...prev.map(m => ({ ...m, isPrimary: false })), method]
            : [...prev, method]);
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4 payment-methods">
                <h3 className="my-auto fw-bold" style={{ fontSize: '20px' }}>Saved Payment Methods</h3>
                <Button
                    onClick={() => setShowAddModal(true)}
                    className="d-flex align-items-center px-4"
                    variant="primary"
                >
                    <span className="me-2"><IoIosAddCircleOutline size={20} /> Add Payment Method</span>
                </Button>
            </div>

            {paymentMethods.map((method) => (
                <Card className='rounded-0' style={{ borderRadius: '#E7ECF1' }}>
                    <div key={method.methodId} className="d-flex align-items-center py-4 px-3">
                        <Form.Check
                            type="radio"
                            id={`payment-method-${method.methodId}`}
                            checked={method.isPrimary}
                            onChange={() => handleSetDefault(method.methodId)}
                            className="me-3"
                        />

                        <div className="d-flex align-items-center flex-grow-1">
                            <div className="me-2">
                                <img src={IconCard} alt="auto" className='me-3 my-auto' width={35} height={28} />
                            </div>

                            <div>
                                <div className='text-primary fw-bold' style={{ fontSize: '16px' }}>
                                    {method.type === 'visa' && 'Visa'} •••• {method.last4}
                                    {method.isPrimary && <span className="ms-2 small rounded-pill text-light py-1 px-2" style={{ backgroundColor: '#E7EFF8' }}>Primary</span>}
                                </div>
                                <small className="text-light" style={{ fontSize: '14px' }}>Expires: {method.expMonth}/{method.expYear}</small>
                                {!method.isPrimary && <div><small className="text-primary fw-bold" style={{ fontSize: '14px' }}>Set as default</small></div>}
                            </div>
                        </div>
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id="tooltip-auto-renewal">
                                    Delete payment method
                                </Tooltip>
                            }
                        >
                            <img type="button" src={IconDelete} alt="auto" className='me-3 my-auto me-md-5' width={20} height={21.18} onClick={() => handleOpenDeleteModal(method.methodId)} />
                        </OverlayTrigger>
                    </div>
                </Card>
            ))}

            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title className='fw-bold'>Add Payment Method</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AddPaymentMethodForm onSubmit={addPaymentMethod} onCancel={() => setShowAddModal(false)} />
                </Modal.Body>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title className='fw-bold'>Delete Payment Method</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this payment method?
                </Modal.Body>
                <Modal.Footer className='border-top-0'>
                    <Button variant="outline-primary" className='px-4' onClick={() => setShowDeleteModal(false)}>No</Button>
                    <Button variant="primary" className='px-4' onClick={handleDelete}>Yes</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const AddPaymentMethodForm = ({ onSubmit, onCancel }) => {

    const formikRef = useRef();

    const [formErrors, setFormErrors] = useState({});
    const stripe = useStripe();
    const elements = useElements();

    const validationSchema = Yup.object({
        cardHolder: Yup.string().required('Card holder name is required'),
        isPrimary: Yup.boolean()
    });

    const validateCardFields = () => {
        const errors = {};
        const cardNumber = elements.getElement(CardNumberElement);
        const cardExpiry = elements.getElement(CardExpiryElement);
        const cardCvc = elements.getElement(CardCvcElement);

        if (!cardNumber || cardNumber._empty) {
            errors.cardNumber = "Card number is required";
        }

        if (!cardExpiry || cardExpiry._empty) {
            errors.cardExpiry = "Expiry date is required";
        }

        if (!cardCvc || cardCvc._empty) {
            errors.cardCvc = "CVC is required";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };


    useEffect(() => {
        if (elements) {
            const cardNumber = elements.getElement(CardNumberElement);
            const cardExpiry = elements.getElement(CardExpiryElement);
            const cardCvc = elements.getElement(CardCvcElement);

            if (cardNumber) {
                cardNumber.on('change', (event) => {
                    if (event.complete) {
                        setFormErrors(prev => ({ ...prev, cardNumber: null }));
                    }
                });
            }

            if (cardExpiry) {
                cardExpiry.on('change', (event) => {
                    if (event.complete) {
                        setFormErrors(prev => ({ ...prev, cardExpiry: null }));
                    }
                });
            }

            if (cardCvc) {
                cardCvc.on('change', (event) => {
                    if (event.complete) {
                        setFormErrors(prev => ({ ...prev, cardCvc: null }));
                    }
                });
            }
        }
    }, [elements]);

    const cardElementOptions = {
        showIcon: true,
        iconStyle: 'solid',
        style: {
            base: {
                fontSize: '16px',
                color: '#000',
                '::placeholder': {
                    color: '#a0aec0',
                },
            },
            invalid: {
                color: '#e53e3e',
            },
        },
        disableLink: true
    };

    const handleSubmit = async (values, { setSubmitting, setErrors }) => {


        if (!stripe || !elements) return;

        if (!validateCardFields()) {
            setSubmitting(false);
            return;
        }

        const cardElement = elements.getElement(CardNumberElement);
        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: { name: values.cardHolder },
        });

        if (error) {
            setErrors({ submit: error.message });
            setSubmitting(false);
            return;
        }

        try {

            const authToken = localStorage.getItem("authToken");
            const userId = localStorage.getItem('userId');
            const res = await axios.post(`${apiConfig.apiBaseURL}/addPaymentMethod`, {
                userId,
                paymentMethodId: paymentMethod.id,
                isPrimary: values.isPrimary
            }, {
                headers: {
                    "x-api-key": apiConfig["x-api-key"],
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${authToken}`
                }
            });


            if (res.data.success) {
                toast.success("Payment method added successfully!", {
                    toastId: "payment-method-added"
                });
                onSubmit({
                    methodId: paymentMethod.id,
                    type: paymentMethod.card.brand,
                    last4: paymentMethod.card.last4,
                    expMonth: paymentMethod.card.exp_month,
                    expYear: paymentMethod.card.exp_year,
                    isPrimary: values.isPrimary
                });
                onCancel();
            }
        } catch (err) {
            console.error(err);
            setErrors({ submit: err.message });
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <Formik innerRef={formikRef} initialValues={{ cardHolder: '', isPrimary: false }} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ isSubmitting, touched, errors, handleChange, values }) => (
                <FormikForm className='credential-page payments'>
                    <Form.Group className="mb-3">
                        <Form.Label>Card Holder Name</Form.Label>
                        <Field name="cardHolder" type="text" className="form-control p-3" placeholder="Enter Card Holder Name" />
                        <ErrorMessage name="cardHolder" component="div" className="text-danger" />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Card Number</Form.Label>
                        <div className="form-control p-3">
                            <CardNumberElement options={cardElementOptions} />
                        </div>
                        {formErrors.cardNumber && (
                            <div className="text-danger">{formErrors.cardNumber}</div>
                        )}
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Expiry Date</Form.Label>
                                <CardExpiryElement className="form-control p-3" />
                                {formErrors.cardExpiry && (
                                    <div className="text-danger">{formErrors.cardExpiry}</div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>CVC</Form.Label>
                                <CardCvcElement className="form-control p-3" />
                                {formErrors.cardCvc && (
                                    <div className="text-danger">{formErrors.cardCvc}</div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-4">
                        <Form.Check
                            type="checkbox"
                            id="isPrimary"
                            name="isPrimary"
                            label="Set as primary"
                            checked={values.isPrimary}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    {errors.submit && (
                        <div className="alert alert-danger">{errors.submit}</div>
                    )}

                    <div className="d-flex justify-content-end">
                        <Button variant="outline-secondary" className="me-2 px-3" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button variant="primary" className='px-3' type="submit" disabled={isSubmitting} onClick={() => {
                            const isValid = validateCardFields();
                            if (isValid && formikRef.current) {
                                formikRef.current.submitForm();
                            }
                        }}
                        >
                            Save
                        </Button>
                    </div>
                </FormikForm>
            )}
        </Formik>
    );
};


export default PaymentMethodsTab;